import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Languages } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";

const navIds = [
  { id: "productos", key: "products" },
  { id: "ritual", key: "ritual" },
  { id: "origen", key: "origin" },
  { id: "faq", key: "faq" },
  { id: "contacto", key: "contact" },
];

export const Header = () => {
  const { t, lang, toggle } = useLanguage();
  const { itemCount, setOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  // En la home hace scroll; en otras páginas navega al home (y a la sección).
  const goHome = () => (location.pathname === "/" ? scrollTo("inicio") : navigate("/"));
  const goSection = (id) =>
    location.pathname === "/" ? scrollTo(id) : navigate(`/#${id}`);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#1A1322]/80 backdrop-blur-2xl border-b border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16 h-24 flex items-center justify-between">
        <button
          data-testid="logo-link"
          onClick={goHome}
          className="group flex items-center"
        >
          <img
            src="/logo-amarillo.png"
            alt="Yulu · Cacao con corazón"
            className="h-20 sm:h-24 w-auto max-w-[300px] object-contain object-left select-none"
            draggable="false"
          />
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {navIds.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-link-${n.id}`}
              onClick={() => goSection(n.id)}
              className="text-sm text-[#F5EFE6]/85 hover:text-[#FBAD18] transition-colors tracking-wide font-medium"
            >
              {t.nav[n.key]}
            </button>
          ))}
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className="text-sm text-[#F5EFE6]/85 hover:text-[#FBAD18] transition-colors tracking-wide font-medium"
          >
            Blog
          </Link>
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="flex items-center gap-1.5 px-3 py-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#FBAD18] hover:text-[#F5EFE6] border border-[#FBAD18]/30 hover:border-[#FBAD18] rounded-full transition-colors"
          >
            <Languages size={14} />
            <span>{lang === "es" ? "ES / EN" : "EN / ES"}</span>
          </button>
          <button
            data-testid="cart-trigger"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1322] bg-[#FBAD18] hover:bg-[#D8910D] rounded-full transition-colors"
          >
            <ShoppingBag size={14} />
            <span className="hidden sm:inline">{t.nav.cart}</span>
            {itemCount > 0 && (
              <span
                data-testid="cart-count-badge"
                className="ml-1 inline-flex min-w-[20px] h-[20px] items-center justify-center text-[10px] bg-[#512C89] text-[#FBAD18] rounded-full px-1 font-bold"
              >
                {itemCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
};
